type EmailFormDebugState = {
  isSending: boolean
  invisible: boolean
}

type ThreadItemDebugState = {
  fold: boolean
  expanded: boolean
  prevFold?: boolean
}

interface EmailFormDebugHandle {
  getDebugRootElement: () => HTMLElement | null
  getDebugState: () => EmailFormDebugState
}

interface ThreadItemDebugHandle {
  getDebugKey: () => string | null
  getDebugState: () => ThreadItemDebugState
  setDebugFoldState: (fold: boolean) => void
  setDebugPrevFold: (prevFold?: boolean) => void
  invokeDebugFoldClick: (times?: number) => void
}

const emailFormRegistry = new Set<EmailFormDebugHandle>()
const threadItemRegistry = new Map<string, ThreadItemDebugHandle>()

function isVisibleElement(elem: HTMLElement | null): boolean {
  if (elem == null || elem.isConnected !== true) return false

  const style = window.getComputedStyle(elem)
  if (style.display === 'none' || style.visibility === 'hidden') return false

  return elem.offsetParent != null || style.position === 'fixed'
}

export function registerEmailFormDebugHandle(handle: EmailFormDebugHandle): void {
  emailFormRegistry.add(handle)
}

export function unregisterEmailFormDebugHandle(handle: EmailFormDebugHandle): void {
  emailFormRegistry.delete(handle)
}

export function getVisibleEmailFormDebugState(): EmailFormDebugState | null {
  const handles = Array.from(emailFormRegistry)
  for (let i = handles.length - 1; i >= 0; i -= 1) {
    const handle = handles[i]
    if (isVisibleElement(handle.getDebugRootElement())) {
      return handle.getDebugState()
    }
  }
  return null
}

export function registerThreadItemDebugHandle(handle: ThreadItemDebugHandle): void {
  const key = handle.getDebugKey()
  if (key == null || key.length === 0) return
  threadItemRegistry.set(key, handle)
}

export function unregisterThreadItemDebugHandle(handle: ThreadItemDebugHandle): void {
  const currentEntries = Array.from(threadItemRegistry.entries())
  currentEntries.forEach(([key, value]) => {
    if (value === handle) {
      threadItemRegistry.delete(key)
    }
  })
}

function getThreadItemHandle(key: string): ThreadItemDebugHandle | null {
  return threadItemRegistry.get(key) ?? null
}

export function getThreadItemDebugState(key: string): ThreadItemDebugState | null {
  return getThreadItemHandle(key)?.getDebugState() ?? null
}

export function setThreadItemDebugFoldState(key: string, fold: boolean): boolean {
  const handle = getThreadItemHandle(key)
  if (handle == null) return false
  handle.setDebugFoldState(fold)
  return true
}

export function setThreadItemDebugPrevFold(key: string, prevFold?: boolean): boolean {
  const handle = getThreadItemHandle(key)
  if (handle == null) return false
  handle.setDebugPrevFold(prevFold)
  return true
}

export function invokeThreadItemDebugFoldClick(key: string, times = 1): boolean {
  const handle = getThreadItemHandle(key)
  if (handle == null) return false
  handle.invokeDebugFoldClick(times)
  return true
}
